import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { memo, FC, useRef, useEffect, ComponentType } from "react";
import { ClassNamesArg, css, cx } from "@/domains/emotion";
import { MdsIconKind } from "@/design-system/components/icon/types";
import { iconDefinitionMapping } from "@/design-system/components/icon/iconDefinitionMapping";
import CloudSlashFlippedIcon from "@/assets/images/cloud-slash-flipped.svg?react";
import CodeBlockIcon from "@/assets/images/code-block.svg?react";
import ZapierLogoIcon from "@/assets/icons/brands/zapier.svg?react";
import ZapierLogoSquareIcon from "@/assets/icons/brands/zapier-square.svg?react";
import FirefliesLogoIcon from "@/assets/icons/brands/fireflies.svg?react";
import TypeformLogoIcon from "@/assets/icons/brands/typeform.svg?react";
import SkipForwardIcon from "@/assets/icons/other/skip-forward.svg?react";
import SkipBackwardIcon from "@/assets/icons/other/skip-backward.svg?react";
import CleanupIcon from "@/assets/icons/other/clean-up.svg?react";
import VoiceModeIcon from "@/assets/icons/other/voice-mode.svg?react";
import AutoOrganizeIcon from "@/assets/icons/other/auto-organize.svg?react";
import BrandIcon from "@/assets/icons/brands/mem-up.svg?react";
import BrandHoverIcon from "@/assets/icons/brands/mem-up-hover.svg?react";
import BrandDisabledIcon from "@/assets/icons/brands/mem-up-disabled.svg?react";
import LogoPlaceholderIcon from "@/assets/icons/brands/mem-logo-placeholder.svg?react";
import LogoTomatoIcon from "@/assets/icons/brands/mem-logo-tomato.svg?react";
import GoogleChromeIcon from "@/assets/icons/brands/google-chrome.png";
import ArrowBrandIcon from "@/assets/icons/other/arrow-brand.svg?react";
import styled from "@emotion/styled";

export interface MdsIconStylingProps {
  className?: ClassNamesArg;
  innerStyles?: {
    Icon?: {
      className?: ClassNamesArg;
    };
  };
}

export interface MdsIconProps extends MdsIconStylingProps {
  kind: MdsIconKind;
  slot?: string;
}

// Will override FA icons
const customIcons: Partial<
  Record<
    MdsIconKind,
    {
      component: ComponentType<React.SVGProps<SVGSVGElement>> | string;
      sizeRatio?: number;
      borderRadius?: string;
    }
  >
> = {
  [MdsIconKind.CloudSlashFlipped]: { component: CloudSlashFlippedIcon },
  [MdsIconKind.CodeBlock]: { component: CodeBlockIcon },
  [MdsIconKind.LogoZapier]: { component: ZapierLogoIcon, sizeRatio: 1.3, borderRadius: "20%" },
  [MdsIconKind.LogoZapierSquare]: {
    component: ZapierLogoSquareIcon,
    sizeRatio: 0.8,
    borderRadius: "20%",
  },
  [MdsIconKind.LogoFireflies]: { component: FirefliesLogoIcon, borderRadius: "20%" },
  [MdsIconKind.LogoTypeform]: { component: TypeformLogoIcon, borderRadius: "20%" },
  [MdsIconKind.SkipForward]: { component: SkipForwardIcon },
  [MdsIconKind.SkipBackward]: { component: SkipBackwardIcon },
  [MdsIconKind.Cleanup]: { component: CleanupIcon },
  [MdsIconKind.GoogleChrome]: { component: GoogleChromeIcon },
  [MdsIconKind.Microphone]: { component: VoiceModeIcon },
  [MdsIconKind.AutoOrganize]: { component: AutoOrganizeIcon },
  [MdsIconKind.Brand]: { component: BrandIcon },
  [MdsIconKind.BrandHover]: { component: BrandHoverIcon },
  [MdsIconKind.BrandTransparent]: { component: BrandDisabledIcon },
  [MdsIconKind.LogoPlaceholder]: { component: LogoPlaceholderIcon, sizeRatio: 1.15 },
  [MdsIconKind.LogoTomato]: { component: LogoTomatoIcon, sizeRatio: 1.15 },
  [MdsIconKind.ArrowBrand]: { component: ArrowBrandIcon },
};

/**
 * @deprecated - Use the newer MDS components instead.
 *
 * - If something doesn't exist yet, please create a new MDS component and
 *   have it reviewed by the team!
 *
 * - If something needs to hook into our `stores`, add it to the /components directory.
 *
 * - If something is a one-off; please colocate it with the component/page that
 *   needs it.
 *
 * - If you have questions, please ask in the #hub-eng-web-team Slack channel.
 */
export const MdsIcon: FC<MdsIconProps> = memo(({ kind, className, innerStyles }) => {
  return (
    <IconWrapper className={cx("mds-icon-wrapper", className)}>
      <Icon kind={kind} innerStyles={innerStyles} />
    </IconWrapper>
  );
});

const Icon = ({ kind, innerStyles }: MdsIconProps) => {
  const iconDefinition = iconDefinitionMapping[kind];
  const { style, ref } = useSpinnerAnimation({ kind });

  if (kind in customIcons) {
    const iconInfo = customIcons[kind]!;
    const IconComponent = iconInfo.component;

    // Handle string (image URL) vs React component
    if (typeof IconComponent === "string") {
      return (
        <img
          ref={ref as React.RefObject<HTMLImageElement>}
          src={IconComponent}
          style={style}
          className={cx(
            fsStyles,
            css({
              width: 16 * (iconInfo.sizeRatio ?? 1),
              height: 16 * (iconInfo.sizeRatio ?? 1),
              borderRadius: iconInfo.borderRadius ?? 0,
            }),
            innerStyles?.Icon?.className
          )}
        />
      );
    }

    return (
      <IconComponent
        ref={ref as React.RefObject<SVGSVGElement>}
        style={style}
        className={cx(
          "mds-icon",
          fsStyles,
          css({
            width: 16 * (iconInfo.sizeRatio ?? 1),
            height: 16 * (iconInfo.sizeRatio ?? 1),
            borderRadius: iconInfo.borderRadius ?? 0,
          }),
          innerStyles?.Icon?.className
        )}
      />
    );
  }

  return (
    <FontAwesomeIcon
      ref={ref as React.RefObject<SVGSVGElement>}
      icon={iconDefinition}
      className={cx("mds-icon", fsStyles, innerStyles?.Icon?.className)}
      style={style}
    />
  );
};

const IconWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  height: 16px;
  width: 16px;
  color: ${({ theme }) => theme.colors.icon.primary};
`;

const fsStyles = css({
  fontSize: "14px",
  lineHeight: 1,
  userSelect: "none",
  draggable: "false",
});

/**
 * Hook that provides unified spinner animation styles that work in both regular DOM and shadow DOM.
 *
 * Detects shadow DOM context and injects keyframes into the appropriate root, then uses
 * inline styles to ensure animations work consistently across all rendering contexts.
 */
const useSpinnerAnimation = ({ kind }: { kind: MdsIconKind }) => {
  const isSpinner = kind === MdsIconKind.Spinner || kind === MdsIconKind.SpinnerSolid;
  const ref = useRef<HTMLImageElement | SVGSVGElement>(null);

  useEffect(() => {
    if (!isSpinner || !ref.current) return;

    const root = ref.current.getRootNode();
    const isShadowRoot = root instanceof ShadowRoot;
    const target = isShadowRoot ? root : document.head;
    const keyframesId = "mds-icon-spin-keyframes";

    // Check if keyframes already exist in the target
    const existingStyle = target.querySelector(`#${keyframesId}`);
    if (!existingStyle) {
      const style = document.createElement("style");
      style.id = keyframesId;
      style.textContent = `
        @keyframes mds-icon-spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `;
      target.appendChild(style);
    }
  }, [isSpinner]);

  const style = isSpinner
    ? {
        animation: "mds-icon-spin 1s linear infinite",
        transformOrigin: "center center",
      }
    : undefined;

  return { isSpinner, style, ref };
};
